import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  buildTemplateQualityGateEventProps,
  type TrackTemplateQualityGate,
} from '@heidi/templates/contracts';

/**
 * The app-side emit host for the template quality attestation
 * (`@heidi/templates`).
 *
 * One emit point, so the two sites that report on the gate cannot drift on
 * property names — and app-side, because a package never imports a tracker
 * (ruling 24). What crosses the seam is the `event_name` and the destination
 * routing; the payload's property names come from
 * `buildTemplateQualityGateEventProps` in `@heidi/templates/contracts`, because
 * they are the event's contract and this file does not own them.
 *
 * `shown` is emitted by the dialog rather than by the guard that asks for it:
 * a surface can reach the guard in a React root with no dialog host, in which
 * case the save hangs and nothing is ever put in front of the clinician.
 * Reporting that as a presentation would hide the one failure mode that leaves
 * every Jest suite green.
 *
 * Snowplow-first with `amplitude: false`. Every `track()` also lands as a
 * Datadog RUM action, which is what the gate-went-dark and fail-open monitors
 * query.
 *
 * WHY THE ENUM MEMBER AND NOT `templateQualityGate.name`. Under D9 ruling 27
 * the name's one declaration site is
 * `packages/templates/src/contributions/analytics.ts`, and an emitter is
 * supposed to read it from there. This one cannot: `event_name` discriminates
 * the app's `TrackingEventV2` union, whose members are typed by string ENUM
 * MEMBERS, and TypeScript does not accept a string literal in an enum member's
 * place — passing `templateQualityGate.name` would not compile, and widening the
 * union's key to `string` would delete the event/props check that makes this
 * call site safe. So the enum member stays and the agreement is ASSERTED
 * instead, in this module's suite, which is what makes the enum a binding rather
 * than a second spelling.
 */
export const trackTemplateQualityGate: TrackTemplateQualityGate = (
  gateStatus,
  context
): void => {
  track({
    event_name: TrackingEventName.TemplateQualityGate,
    props: buildTemplateQualityGateEventProps(gateStatus, context),
    options: { amplitude: false },
  });
};
